/**
 * StatusDashboard - Dashboard cruscotto with status icons
 * External line: Controller + External Camera sections. Built-in line: a single
 * capture unit section (the phone is both controller and camera).
 */

import { MaterialIcon } from '@/components/MaterialIcon';
import { CameraImage } from '@/components/CameraImage';
import { AndroidStatus, CameraConfig, CameraMode, GpsLocation, GpsError, View } from '@/types';
import { StatusDashboardIcon } from './StatusDashboardIcon';
import { ControllerIcon } from './ControllerIcon';
import { getViewStatusItems, type StatusDisplayItem } from './statusItems';
import styles from './StatusDashboard.module.css';

interface StatusDashboardProps {
  androidStatus: AndroidStatus | null | undefined;
  cameraSettings?: CameraConfig;
  cameraMode?: CameraMode;
  /** GPS location data from view */
  gpsLocation?: GpsLocation;
  /** Last GPS error reported by device (used to distinguish failure modes) */
  gpsError?: GpsError;
  /** Whether device is currently online */
  isOnline?: boolean;
  /** Power board state (from view doc) — source of the ambient sensor readings in External mode. */
  masterPower?: View['masterPower'];
  /** Called when an icon is clicked, with the item ID */
  onIconClick?: (itemId: string) => void;
  /** Called when camera settings link is clicked */
  onCameraSettingsClick?: () => void;
}

export const StatusDashboard: React.FC<StatusDashboardProps> = ({
  androidStatus,
  cameraSettings,
  cameraMode,
  gpsLocation,
  gpsError,
  isOnline,
  masterPower,
  onIconClick,
  onCameraSettingsClick,
}) => {
  const isExternal = cameraMode === 'dslr';
  const { controllerItems, cameraItems } = getViewStatusItems({
    androidStatus,
    cameraSettings,
    cameraMode,
    gpsLocation,
    gpsError,
    isOnline,
    masterPower,
  });

  const dslrInfo = androidStatus?.device?.dslr;
  const dslrConnected = !!dslrInfo?.connected;
  const cameraModel = dslrConnected ? dslrInfo?.model || 'Unknown' : 'Not connected';

  const renderIcons = (items: StatusDisplayItem[]) =>
    items.map((item) => (
      <StatusDashboardIcon
        key={item.id}
        icon={item.icon}
        textIcon={item.textIcon}
        textIconStrike={item.textIconStrike}
        status={item.status}
        tooltip={item.tooltip}
        onClick={() => onIconClick?.(item.id)}
      />
    ));

  // Built-in line: single capture unit section
  if (!isExternal) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.section}>
          <div className={styles.sectionHeaderStatic}>
            <MaterialIcon name="mobile_camera" size={18} />
            <span className={styles.sectionLabel}>CAPTURE UNIT</span>
            {onCameraSettingsClick && (
              <span className={styles.settingsLink} onClick={onCameraSettingsClick}>
                Settings
              </span>
            )}
          </div>
          <div className={styles.iconsRow}>{renderIcons(controllerItems)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.subSections}>
      {/* Controller section */}
      <div className={styles.section}>
        <div className={styles.sectionHeaderStatic}>
          <ControllerIcon />
          <span className={styles.sectionLabel}>CONTROLLER</span>
        </div>
        <div className={styles.iconsRow}>{renderIcons(controllerItems)}</div>
      </div>

      {/* Separator */}
      <div className={styles.separator} />

      {/* External camera section — connected camera (name above picture) in the bottom-right corner */}
      <div className={styles.cameraSectionRow}>
        <div className={styles.section}>
          <div className={styles.sectionHeaderStatic}>
            <MaterialIcon name="photo_camera" size={18} />
            <span className={styles.sectionLabel}>EXTERNAL CAMERA</span>
          </div>
          <div className={styles.iconsRow}>{renderIcons(cameraItems)}</div>
        </div>
        <div className={styles.cameraBadge}>
          <span className={styles.cameraBadgeName} title={cameraModel}>{cameraModel}</span>
          {/* Picture opens the camera settings */}
          <span
            className={onCameraSettingsClick ? styles.cameraBadgeLink : undefined}
            onClick={onCameraSettingsClick}
            role={onCameraSettingsClick ? 'button' : undefined}
            tabIndex={onCameraSettingsClick ? 0 : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onCameraSettingsClick?.();
            }}
          >
            <CameraImage
              modelName={dslrConnected ? dslrInfo?.model : null}
              manufacturer={dslrConnected ? dslrInfo?.manufacturer : null}
              variant="badge"
            />
          </span>
        </div>
      </div>
      </div>
    </div>
  );
};
